'use client'

import React, { useEffect, useState } from 'react'
import { IoClose } from 'react-icons/io5'

interface DrawerProps {
    isOpen: boolean
    onClose: () => void
    title?: string
    children: React.ReactNode
    width?: string
}

export default function Drawer({
    isOpen,
    onClose,
    title,
    children,
    width = 'w-96',
}: DrawerProps) {
    const [isVisible, setIsVisible] = useState(false)
    const [isAnimating, setIsAnimating] = useState(false)

    useEffect(() => {
        if (isOpen) {
            setIsVisible(true)
            document.body.style.overflow = 'hidden'
            // Trigger animation after component is visible
            setTimeout(() => setIsAnimating(true), 10)
        } else {
            setIsAnimating(false)
            document.body.style.overflow = 'unset'
            // Hide component after animation completes
            setTimeout(() => setIsVisible(false), 300)
        }

        return () => {
            document.body.style.overflow = 'unset'
        }
    }, [isOpen])

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose()
            }
        }

        if (isOpen) {
            document.addEventListener('keydown', handleEscape)
        }

        return () => {
            document.removeEventListener('keydown', handleEscape)
        }
    }, [isOpen, onClose])

    if (!isVisible) return null

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black/50 transition-opacity duration-300 ${
                    isAnimating ? 'opacity-100' : 'opacity-0'
                }`}
                onClick={onClose}
            />

            {/* Drawer */}
            <div className="fixed right-0 top-0 h-full flex">
                <div
                    className={`${width} max-w-full bg-white shadow-xl transform transition-transform ease-in-out duration-300 flex flex-col ${
                        isAnimating ? 'translate-x-0' : 'translate-x-full'
                    }`}
                >
                    {/* Header */}
                    {title && (
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900">
                                {title}
                            </h2>
                            <button
                                onClick={onClose}
                                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <IoClose className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto">{children}</div>
                </div>
            </div>
        </div>
    )
}
